// @flow
import * as React from 'react';
import type { Node as ReactNode } from 'react';
import ReactModal from 'react-modal';
import styled, {
  type StyledComponent,
  ThemeContext,
  type PropsWithTheme,
} from 'styled-components';
import invariant from 'invariant';
import noop from 'lodash/fp/noop';

import Text from 'ui/design-system/text';
import type { Theme } from 'ui/styles';
import Button from 'ui/design-system/button';
import Flex from 'ui/private/flex';
import type { ROA } from 'modules/type';
import CloseButton from 'ui/private/close-button';
// we need to wrap everything with base styles because there's a portal in play here
import { BaseCssProvider } from 'ui/scramble4-connect';

type Styles = { +[key: string]: string | number };

const Header = styled(Flex)`
  padding: 24px 24px 12px 24px;
  background: #f6f6f8;
  background: ${({ background }: { background: string }) => background};
  border-radius: ${({ borderRadius }: { borderRadius?: string }) =>
    borderRadius || 0};
`;

const ModalTitle = styled(Text)`
  flex-grow: 1;
  line-height: 23px;
`;

const Subtitle = styled(Text).attrs({ size: 3, color: 'gray_4' })``;

const CloseButtonWrapper = styled.div`
  text-align: end;
  position: absolute;
  top: 21px;
  right: 16px;
`;

type ActionsProps = $ReadOnly<{|
  padding?: string,
  borderRadius?: string,
|}>;

export const Actions: StyledComponent<
  ActionsProps,
  Theme,
  HTMLDivElement,
> = styled.div`
  text-align: right;
  background: ${({ theme }: PropsWithTheme<ActionsProps, Theme>) =>
    theme.gray_1};
  padding: ${({ padding }: PropsWithTheme<ActionsProps, Theme>) =>
    padding || '24px'};
  border-radius: ${({ borderRadius }: { borderRadius?: string }) =>
    borderRadius || 0};
`;

export const ContentWrapper: StyledComponent<
  {||},
  Theme,
  typeof BaseCssProvider,
> = styled(BaseCssProvider)`
  flex: 1 0 auto;
`;

const ActionButton = styled(Button)`
  :first-child {
    margin-left: 0;
  }
  :last-child {
    margin-right: 0;
  }
  margin: 0 4px;
`;

export type Instance = ReactModal;

export type Size = 'small' | 'default' | 'large' | 'x-large' | 'max';

export type ModalAction = {
  /* eslint-disable react/no-unused-prop-types */
  handleClick: () => void,
  label: string | ReactNode,
  type: 'primary' | 'secondary' | 'danger' | 'tertiary',
  disabled?: boolean,
  /* eslint-enable react/no-unused-prop-types */
};

export type Props = $ReadOnly<{|
  isOpen: boolean,
  children?: ReactNode,
  actions?: ROA<ModalAction>,
  actionsText?: ReactNode,
  onRequestClose: () => void,
  contentLabel?: React.Node,
  appElement?: HTMLElement,
  size?: Size,
  buttonsAreSmall?: boolean,
  contentPadding?: string,
  actionsPadding?: string,
  style?: {
    overlay?: Styles,
    /* eslint-disable react/no-unused-prop-types */
    content?: Styles,
    closeButton?: Styles,
    /* eslint-enable react/no-unused-prop-types */
  },
  overflow?: 'auto' | 'visible' | 'hidden',
  position?: 'static' | 'absolute' | 'fixed' | 'relative',
  title?: React.Node,
  subtitle?: () => React.Node,
  usesCornerCloseButton?: boolean,
  shouldCloseOnOverlayClick?: boolean,
  shouldCloseOnEsc?: boolean,
  hasHeader?: boolean,
  overlayColor?: string,
  margin?: string,
  positionTop?: string,
  positionLeft?: string,
  positionRight?: string,
  onAfterClose?: () => void,
  onAfterOpen?: () => void,
  renderActionButtons?: () => React.Node,
  fullWidth?: boolean,
  headerBackground?: string,
|}>;

const Content = styled.div`
  padding: ${({ padding }: { padding?: string }) =>
    padding || '12px 24px 24px 24px'};
  overflow: ${({ contentOverflow }: { contentOverflow?: string }) =>
    contentOverflow || 'auto'};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
`;

const TitleWrapper = styled(Flex)``;

export const width: Map<Size, string> = new Map<Size, string>([
  ['small', '300px'],
  ['default', '450px'],
  ['large', '764px'],
  ['x-large', '1077px'],
]);

const Modal = (
  {
    isOpen,
    children,
    actions,
    actionsText,
    onRequestClose,
    contentLabel,
    appElement,
    // eslint-disable-next-line no-unused-vars
    overflow = 'auto',
    position = 'absolute',
    size = 'default',
    style = { ...null },
    title,
    subtitle,
    usesCornerCloseButton = true,
    hasHeader = true,
    contentPadding = '12px 24px 24px 24px',
    actionsPadding = '24px',
    shouldCloseOnEsc,
    shouldCloseOnOverlayClick,
    overlayColor,
    margin = 'auto',
    positionLeft = '',
    positionRight = '',
    positionTop = '',
    onAfterClose = noop,
    onAfterOpen = noop,
    buttonsAreSmall = false,
    renderActionButtons,
    fullWidth = false,
    headerBackground = '#fff',
  }: Props,
  forwardedRef:
    | ((null | Instance) => mixed)
    | { current: null | Instance, ... },
) => {
  const theme = React.useContext(ThemeContext);
  invariant(document.body, 'document.body must exist');

  invariant(
    !(
      Array.isArray(actions) &&
      actions.length > 0 &&
      typeof renderActionButtons === 'function'
    ),
    'One or more action button definitions and a render prop were passed to display buttons. Pick one.',
  );

  const documentBody = document.body;
  const attachmentPoint = appElement || documentBody;
  const mergedStyles = {
    overlay: {
      ...({
        backgroundColor: overlayColor || theme.overlay,
        position,
        zIndex: 999,
      }: Styles),
      ...(style.overlay || { ...null }: Styles),
    },
    content: {
      ...({
        border: 'none',
        borderRadius: '0',
        boxShadow:
          ((theme.elevations: any): Map<number, string>).get(5) || 'unset',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
      }: Styles),
      ...((size !== 'max'
        ? {
            margin,
            bottom: 'auto',
            maxHeight: '80vh',
            maxWidth: fullWidth ? '100vw' : '90vw',
            width: width.get(size) || 'unset',
          }
        : { ...null }): Styles),
      ...(style.content || { ...null }: Styles),
      ...((positionTop !== ''
        ? {
            top: positionTop,
          }
        : { ...null }): Styles),
      ...((positionLeft !== ''
        ? {
            left: positionLeft,
          }
        : { ...null }): Styles),
      ...((positionRight !== ''
        ? {
            right: positionRight,
          }
        : { ...null }): Styles),
    },
  };
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      ref={forwardedRef}
      contentLabel={contentLabel}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      {
        ...{ ...null }
        // TODO: should not be passing ariaHideApp={false} below,
        // but I'm not sure if we have a good alternative currently.
      }
      ariaHideApp={false}
      parentSelector={() => attachmentPoint}
      style={mergedStyles}
    >
      {hasHeader && (
        <Header
          background={headerBackground}
          borderRadius={style?.content?.borderRadius}
        >
          <BaseCssProvider>
            <TitleWrapper flexDirection="column" childSpacing="8px">
              <ModalTitle weight="normal" size={5}>
                {title}
              </ModalTitle>
              {subtitle && <Subtitle>{subtitle()}</Subtitle>}
            </TitleWrapper>
            {usesCornerCloseButton && (
              <CloseButtonWrapper>
                <CloseButton
                  onClick={onRequestClose}
                  size={24}
                  data-testid="closeModalButton"
                />
              </CloseButtonWrapper>
            )}
          </BaseCssProvider>
        </Header>
      )}
      <Content padding={contentPadding} contentOverflow={overflow}>
        <ContentWrapper>{children}</ContentWrapper>
      </Content>
      {actions && actions.length > 0 && (
        // $FlowFixMe[incompatible-use]
        <Actions
          padding={actionsPadding}
          borderRadius={style?.content?.borderRadius}
        >
          <BaseCssProvider>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {actionsText}
              {actions.map(({ label, type, handleClick, disabled }, idx) => (
                // TODO: should not be using idx as key below, won't be able to
                // reliably rearrange buttons, but how often are we gonna want to do
                // that?
                <ActionButton
                  // TODO: maybe make the key a property of the action?
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  primary={type === 'primary'}
                  secondary={type === 'secondary'}
                  danger={type === 'danger'}
                  onClick={handleClick}
                  disabled={disabled}
                  {...(buttonsAreSmall && { small: true })}
                >
                  {label}
                </ActionButton>
              ))}
            </div>
          </BaseCssProvider>
        </Actions>
      )}
      {renderActionButtons && renderActionButtons()}
    </ReactModal>
  );
};

export default (React.forwardRef<Props, Instance>(
  Modal,
): React$AbstractComponent<Props, Instance>);
